'use client';

import { reportError } from '@/utils/error-reporting';
import { useTenantSelection } from '@payloadcms/plugin-multi-tenant/client';
import { Button, FieldDescription, LoadingOverlay, toast, useDocumentInfo, useField, useFormFields, useLocale } from '@payloadcms/ui';
import React, { useCallback, useMemo, useState } from 'react';

import { convertReferenceToInlineForOtherLocales } from '../actions/convert-reference-to-inline';

import type { FieldAction } from '@payloadcms/ui';
import type { FormState, Row, UIFieldClientComponent } from 'payload';
import type { Dispatch } from 'react';

const REFERENCE_TO_INLINE_BUTTON_FIELD_NAME = 'referenceToInlineButton';

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const isBlocksOrArrayField = (value: unknown): value is Record<string, unknown>[] => {
  return Array.isArray(value) && value.length > 0 && value.every((item) => isPlainObject(item));
};

const isRichTextField = (value: unknown): boolean => {
  if (!isPlainObject(value)) return false;
  const root = value.root;
  if (!isPlainObject(root)) return false;
  return root.type === 'root' && Array.isArray(root.children);
};

const generateRowId = (): string => crypto.randomUUID();

const processArrayValue = (items: Record<string, unknown>[]): { rows: Row[]; value: Record<string, unknown>[] } => {
  const processedItems: Record<string, unknown>[] = [];
  const rows: Row[] = [];

  for (const item of items) {
    const id = typeof item.id === 'string' ? item.id : generateRowId();
    const processedItem = { ...item, id };
    processedItems.push(processedItem);

    rows.push({
      id,
      blockType: typeof item.blockType === 'string' ? item.blockType : undefined,
      collapsed: false,
    });
  }

  return { value: processedItems, rows };
};

const flattenAndDispatch = (obj: Record<string, unknown>, basePath: string, dispatch: Dispatch<FieldAction>): void => {
  for (const [key, value] of Object.entries(obj)) {
    if (key === 'id' || key === 'createdAt' || key === 'updatedAt' || key === 'tenant' || key === 'status') {
      continue;
    }

    if (value === undefined) {
      continue;
    }

    const fieldPath = `${basePath}.${key}`;

    if (isBlocksOrArrayField(value)) {
      const { value: processedValue, rows } = processArrayValue(value);

      dispatch({
        type: 'UPDATE',
        path: fieldPath,
        value: processedValue,
        rows,
      });

      processedValue.forEach((item, index) => {
        flattenAndDispatch(item, `${fieldPath}.${index}`, dispatch);
      });
    } else if (Array.isArray(value)) {
      dispatch({
        type: 'UPDATE',
        path: fieldPath,
        value,
        rows: [],
      });
    } else if (isRichTextField(value)) {
      dispatch({
        type: 'UPDATE',
        path: fieldPath,
        value,
      });
    } else if (isPlainObject(value)) {
      flattenAndDispatch(value, fieldPath, dispatch);
    } else {
      dispatch({
        type: 'UPDATE',
        path: fieldPath,
        value,
      });
    }
  }
};

const ReferenceModuleToInlineButton: UIFieldClientComponent = ({ path }) => {
  const [isConverting, setIsConverting] = useState<boolean>(false);
  const { id: pageId } = useDocumentInfo();
  const locale = useLocale();
  const { selectedTenantID } = useTenantSelection();

  const BASE_KEY_PATH = useMemo(() => {
    const suffix = `.${REFERENCE_TO_INLINE_BUTTON_FIELD_NAME}`;
    return path.endsWith(suffix) ? path.slice(0, -suffix.length) : path;
  }, [path]);

  const moduleIndex = useMemo(() => {
    const parts = BASE_KEY_PATH.split('.');
    return parseInt(parts[parts.length - 1], 10);
  }, [BASE_KEY_PATH]);

  const BLOCK_TYPE_KEY = `${BASE_KEY_PATH}.blockType`;
  const SOURCE_TYPE_KEY = `${BASE_KEY_PATH}.sourceType`;
  const REFERENCE_KEY = `${BASE_KEY_PATH}.reference`;
  const INLINE_CONTENT_PATH = `${BASE_KEY_PATH}.inlineContent`;

  const [fields, dispatch] = useFormFields(([fields, dispatch]) => {
    const matchingFields: FormState = {};
    Object.entries(fields).forEach(([key, value]) => {
      if (key.startsWith(BASE_KEY_PATH)) {
        matchingFields[key] = value;
      }
    });
    return [matchingFields, dispatch] as [FormState, Dispatch<FieldAction>];
  });

  const { setValue: setSourceTypeValue } = useField({ path: SOURCE_TYPE_KEY });
  const { setValue: setReferenceValue } = useField({ path: REFERENCE_KEY });

  const handleConvertReferenceToInline = useCallback(async () => {
    if (isConverting) return;
    setIsConverting(true);

    try {
      const COLLECTION_SLUG = fields[BLOCK_TYPE_KEY]?.value as string | undefined;
      const REFERENCE_ID = fields[REFERENCE_KEY]?.value as string | undefined;

      if (!COLLECTION_SLUG) {
        toast.error('Unable to determine module type.');
        return;
      }
      if (!REFERENCE_ID) {
        toast.error('Select a module to convert first.');
        return;
      }

      const response = await fetch(`/api/${COLLECTION_SLUG}/${REFERENCE_ID}?depth=0&locale=${locale.code}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        toast.error('Failed to fetch the referenced module.');
        return;
      }

      const doc = (await response.json()) as Record<string, unknown>;

      flattenAndDispatch(doc, INLINE_CONTENT_PATH, dispatch);

      await setSourceTypeValue('inline');
      await setReferenceValue(undefined);

      const { succeededLocales, failedLocales } = await convertReferenceToInlineForOtherLocales({
        currentLocale: locale.code,
        moduleIndex,
        pageId,
        referencedCollectionSlug: COLLECTION_SLUG,
        referencedDocId: REFERENCE_ID,
        tenantId: selectedTenantID,
      });

      const allSucceeded = [locale.code, ...succeededLocales];
      toast.success(`Converted to inline for: ${allSucceeded.join(', ')} locales.`);

      if (failedLocales.length > 0) {
        toast.error(`Failed to convert for: ${failedLocales.join(', ')} locales.`);
      }
    } catch (error) {
      reportError(error, {
        flow: 'reference-to-inline',
        collection_slug: fields[BLOCK_TYPE_KEY]?.value as string | undefined,
        reference_id: fields[REFERENCE_KEY]?.value as string | undefined,
        page_id: pageId,
        locale: locale.code,
        module_index: moduleIndex,
        tenant_id: selectedTenantID,
      });
      toast.error('Failed to convert to inline.');
    } finally {
      setIsConverting(false);
    }
  }, [
    isConverting,
    fields,
    BLOCK_TYPE_KEY,
    REFERENCE_KEY,
    INLINE_CONTENT_PATH,
    dispatch,
    setReferenceValue,
    setSourceTypeValue,
    pageId,
    locale.code,
    moduleIndex,
    selectedTenantID,
  ]);

  return (
    <div>
      {isConverting && <LoadingOverlay />}
      <Button onClick={handleConvertReferenceToInline} buttonStyle="secondary" icon="swap" disabled={isConverting}>
        {isConverting ? 'Converting...' : 'Convert to inline content'}
      </Button>
      <FieldDescription path={path} description="This will copy the fields from the selected shared module into the inline content." />
    </div>
  );
};

export default ReferenceModuleToInlineButton;
